import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  createHallVendor,
  deleteHallVendor,
  getHallVendors,
  updateHallVendor,
} from '../api';
import { getHotelConfiguration } from '../../../services/api';
import { DataTable } from '../../../shared';
import countryCodesData from '../../../data/country-codes.json';
import countriesData from '../../../data/countries-states-cities-data';
import { cityFilterOptions, countryFilterOptions, stateFilterOptions } from '../../../utils/locationSearch';
import {
  getCountryCodeMeta,
  getPhoneMaxLength,
  normalizePhoneInputForCountry,
  splitPhoneWithDialCode,
  validatePhoneForCountry,
} from '../../../utils/phoneValidation';
import {
  HALL_DRAWER_FIELD_GAP,
  HALL_FORM_ACCENT,
  getHallDrawerPaperSx,
  getHallFormInputSx,
} from '../utils/hallDrawerStyles';
import { getHallApiErrorMessage } from '../utils/hallErrors';

const VENDOR_TYPES = [
  { value: 'decoration', label: 'Decoration' },
  { value: 'catering', label: 'Catering' },
  { value: 'photography', label: 'Photography' },
  { value: 'videography', label: 'Videography' },
  { value: 'music', label: 'Music / DJ' },
  { value: 'lighting_sound', label: 'Lighting & Sound' },
  { value: 'floral', label: 'Floral' },
  { value: 'tent_mandap', label: 'Tent & Mandap' },
  { value: 'stage_setup', label: 'Stage Setup' },
  { value: 'furniture_rental', label: 'Furniture Rental' },
  { value: 'makeup_beauty', label: 'Makeup & Beauty' },
  { value: 'entertainment', label: 'Entertainment / Emcee' },
  { value: 'security', label: 'Security' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'power_generator', label: 'Power / Generator' },
  { value: 'bartending', label: 'Bartending' },
  { value: 'waitstaff', label: 'Waitstaff' },
  { value: 'printing', label: 'Printing & Invitations' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'other', label: 'Other' },
];

/** Catalog of hall-related services shown as selectable chips (stored on vendor.services). */
const HALL_VENDOR_SERVICE_OPTIONS = [
  'Stage decoration',
  'Theme décor',
  'Floral arrangement',
  'Mandap / backdrop',
  'Balloon décor',
  'Entrance décor',
  'Buffet setup',
  'Live counters',
  'Menu planning',
  'Cake & desserts',
  'Photography',
  'Videography',
  'Candid shoot',
  'Drone coverage',
  'DJ / music',
  'Live band',
  'Sound system',
  'Lighting',
  'LED wall',
  'Projector / screen',
  'Mic / AV technician',
  'Tent & canopy',
  'Stage setup',
  'Furniture rental',
  'Extra chairs',
  'Extra tables',
  'Bridal makeup',
  'Groom makeup',
  'Emcee / anchor',
  'Dance troupe',
  'Security staff',
  'Valet',
  'Parking management',
  'Guest transportation',
  'Generator / power',
  'Bartending',
  'Waitstaff',
  'Invitation printing',
  'Housekeeping',
  'Deep cleaning',
  'AC overtime',
  'Overtime support',
];

const SERVICES_BY_VENDOR_TYPE = {
  decoration: ['Stage decoration', 'Theme décor', 'Floral arrangement', 'Mandap / backdrop', 'Balloon décor', 'Entrance décor'],
  catering: ['Buffet setup', 'Live counters', 'Menu planning', 'Cake & desserts', 'Waitstaff', 'Bartending'],
  photography: ['Photography', 'Candid shoot', 'Drone coverage'],
  videography: ['Videography', 'Drone coverage', 'Candid shoot'],
  music: ['DJ / music', 'Live band', 'Sound system'],
  lighting_sound: ['Lighting', 'Sound system', 'LED wall', 'Projector / screen', 'Mic / AV technician'],
  floral: ['Floral arrangement', 'Mandap / backdrop', 'Entrance décor'],
  tent_mandap: ['Tent & canopy', 'Mandap / backdrop', 'Stage setup'],
  stage_setup: ['Stage setup', 'Stage decoration', 'LED wall', 'Lighting'],
  furniture_rental: ['Furniture rental', 'Extra chairs', 'Extra tables'],
  makeup_beauty: ['Bridal makeup', 'Groom makeup'],
  entertainment: ['Emcee / anchor', 'Dance troupe', 'DJ / music', 'Live band'],
  security: ['Security staff', 'Valet', 'Parking management'],
  transportation: ['Guest transportation', 'Valet', 'Parking management'],
  power_generator: ['Generator / power', 'AC overtime'],
  bartending: ['Bartending', 'Waitstaff', 'Live counters'],
  waitstaff: ['Waitstaff', 'Buffet setup', 'Bartending'],
  printing: ['Invitation printing'],
  housekeeping: ['Housekeeping', 'Deep cleaning'],
  other: HALL_VENDOR_SERVICE_OPTIONS,
};

const STATUS_VALUES = ['active', 'inactive'];
const COUNTRY_OPTIONS = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
const COUNTRY_CODE_OPTIONS = Array.isArray(countryCodesData) ? countryCodesData : [];

const defaultVendorForm = {
  vendor_code: '',
  name: '',
  vendor_type: 'decoration',
  phone: '',
  phone_country_code: '+91',
  email: '',
  gstin: '',
  country: '',
  state: '',
  city: '',
  address: '',
  serviceTags: [],
  is_active: true,
  notes: '',
};

function resolveCountryNameFromHotelCode(countryCode) {
  if (!countryCode) return '';
  const normalizedCode = String(countryCode).trim().toUpperCase();
  const matchedCountry = COUNTRY_OPTIONS.find(
    (country) => country?.code?.toUpperCase() === normalizedCode,
  );
  return matchedCountry?.name || '';
}

function inferCountryFromStateCity(stateName, cityName) {
  const normalizedState = String(stateName || '').trim().toLowerCase();
  const normalizedCity = String(cityName || '').trim().toLowerCase();

  for (const country of COUNTRY_OPTIONS) {
    const states = Array.isArray(country?.states) ? country.states : [];
    for (const state of states) {
      const stateMatch = normalizedState
        && String(state?.name || '').trim().toLowerCase() === normalizedState;
      const cityMatch = normalizedCity
        && Array.isArray(state?.cities)
        && state.cities.some((city) => String(city || '').trim().toLowerCase() === normalizedCity);
      if (stateMatch || cityMatch) {
        return country?.name || '';
      }
    }
  }

  return '';
}

function formatVendorPhoneDisplay(row) {
  const code = String(row?.phone_country_code || '').trim();
  const number = String(row?.phone || '').trim();
  if (!number) return '-';
  return code ? `${code} ${number}` : number;
}

function parseServiceTags(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function serializeServiceTags(tags) {
  return parseServiceTags(tags).join(', ');
}

function isServiceSelected(tags, service) {
  const target = String(service || '').trim().toLowerCase();
  return parseServiceTags(tags).some((tag) => tag.toLowerCase() === target);
}

function getServiceOptionsForVendorType(vendorType) {
  const related = SERVICES_BY_VENDOR_TYPE[vendorType] || [];
  const seen = new Set(related.map((item) => item.toLowerCase()));
  const rest = HALL_VENDOR_SERVICE_OPTIONS.filter((item) => !seen.has(item.toLowerCase()));
  return [...related, ...rest];
}

function normalizeVendorRow(row) {
  return {
    id: row?.id,
    vendor_code: row?.vendor_code || '',
    name: row?.name || '',
    vendor_type: row?.vendor_type || 'other',
    vendor_type_label: row?.vendor_type_label || '',
    phone: row?.phone || '',
    phone_country_code: row?.phone_country_code || '+91',
    email: row?.email || '',
    gstin: row?.gstin || '',
    country: row?.country || '',
    state: row?.state || '',
    city: row?.city || '',
    address: row?.address || '',
    serviceTags: parseServiceTags(row?.services),
    services: row?.services || '',
    status: row?.status || (row?.is_active === false ? 'inactive' : 'active'),
    notes: row?.notes || '',
  };
}

function isVendorFormValid(form) {
  if (!form.name.trim() || !form.vendor_type || !form.phone.trim()) return false;
  return !validatePhoneForCountry(form.phone, form.phone_country_code, { label: 'Phone' });
}

const HALL_ACCENT = HALL_FORM_ACCENT;

function statusChipSx(status, isDark) {
  const color = status === 'active' ? '#4ce1b6' : '#94a3b8';
  return {
    color,
    bgcolor: alpha(color, isDark ? 0.18 : 0.12),
    border: `1px solid ${alpha(color, 0.35)}`,
    fontWeight: 800,
    textTransform: 'capitalize',
  };
}

function vendorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'V';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function VendorViewInfoTile({ icon, label, value, isDark }) {
  const display = String(value || '').trim() || '—';
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        p: 1.25,
        borderRadius: 2,
        bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.03),
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        minHeight: 72,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: alpha(HALL_ACCENT, isDark ? 0.2 : 0.14),
          color: isDark ? HALL_ACCENT : '#0f766e',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: isDark ? '#f1f5f9' : '#0f172a',
            fontWeight: 700,
            fontSize: '0.92rem',
            wordBreak: 'break-word',
            lineHeight: 1.35,
          }}
        >
          {display}
        </Typography>
      </Box>
    </Box>
  );
}

function VendorViewSection({ title, icon, children, isDark, action = null }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
        bgcolor: isDark ? alpha('#0b1220', 0.55) : '#fff',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1.1,
          bgcolor: isDark ? alpha(HALL_ACCENT, 0.1) : alpha(HALL_ACCENT, 0.08),
          borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: HALL_ACCENT, display: 'grid', placeItems: 'center' }}>{icon}</Box>
          <Typography sx={{ fontWeight: 900, fontSize: '0.92rem', color: isDark ? '#f8fafc' : '#0f172a' }}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
}

export default function HallVendorManagementWorkspace({ isDark, tokens, surfaceSx, onShowMessage }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultVendorForm);
  const [defaultCountryName, setDefaultCountryName] = useState('');
  const [defaultDialCode, setDefaultDialCode] = useState('+91');
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [customServiceInput, setCustomServiceInput] = useState('');
  const phoneCountryCodeAnchorRef = useRef(null);

  const showMessage = useCallback((severity, message) => {
    if (typeof onShowMessage === 'function') {
      onShowMessage(severity, message);
    }
  }, [onShowMessage]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getHallVendors({ include_inactive: true });
      const results = Array.isArray(payload?.results) ? payload.results.map(normalizeVendorRow) : [];
      setRows(results);
    } catch (err) {
      showMessage('error', getHallApiErrorMessage(err, 'Unable to load vendors.'));
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let isMounted = true;

    const loadHotelLocationDefaults = async () => {
      try {
        const hotelConfig = await getHotelConfiguration();
        if (!isMounted) return;

        const hotelCountryCode = hotelConfig?.country || '';
        const resolvedCountryName = resolveCountryNameFromHotelCode(hotelCountryCode);
        const matchedDialCode = COUNTRY_CODE_OPTIONS.find(
          (country) => country?.code?.toUpperCase() === String(hotelCountryCode || '').toUpperCase(),
        );

        if (resolvedCountryName) {
          setDefaultCountryName(resolvedCountryName);
        }
        if (matchedDialCode?.dialCode) {
          setDefaultDialCode(matchedDialCode.dialCode);
        }
      } catch (error) {
        // Keep India defaults when hotel configuration is unavailable.
      }
    };

    loadHotelLocationDefaults();
    return () => {
      isMounted = false;
    };
  }, []);

  const buildFormFromRow = useCallback((row) => {
    const normalized = normalizeVendorRow(row);
    const fallbackDial = normalized.phone_country_code || defaultDialCode;
    const phoneParts = splitPhoneWithDialCode(normalized.phone, fallbackDial);
    const country = normalized.country
      || inferCountryFromStateCity(normalized.state, normalized.city)
      || defaultCountryName
      || '';

    return {
      vendor_code: normalized.vendor_code,
      name: normalized.name,
      vendor_type: normalized.vendor_type,
      phone: phoneParts.nationalNumber,
      phone_country_code: normalized.phone_country_code || phoneParts.dialCode || defaultDialCode,
      email: normalized.email,
      gstin: normalized.gstin,
      country,
      state: normalized.state,
      city: normalized.city,
      address: normalized.address,
      serviceTags: normalized.serviceTags,
      is_active: normalized.status === 'active',
      notes: normalized.notes,
    };
  }, [defaultCountryName, defaultDialCode]);

  const getFreshFormData = useCallback(
    () => ({
      ...defaultVendorForm,
      country: defaultCountryName || '',
      phone_country_code: defaultDialCode || '+91',
    }),
    [defaultCountryName, defaultDialCode],
  );

  const selectedPhoneCountryCode = useMemo(
    () => getCountryCodeMeta(form.phone_country_code),
    [form.phone_country_code],
  );

  const nationalPhoneMax = useMemo(
    () => getPhoneMaxLength(form.phone_country_code),
    [form.phone_country_code],
  );

  const phoneHelperText = useMemo(
    () => validatePhoneForCountry(form.phone, form.phone_country_code, { label: 'Phone', required: false }),
    [form.phone, form.phone_country_code],
  );

  const selectedCountryObj = useMemo(
    () => COUNTRY_OPTIONS.find(
      (country) => country?.name === form.country
        || country?.code === form.country
        || country?.name?.toLowerCase() === String(form.country || '').toLowerCase(),
    ) || null,
    [form.country],
  );

  const stateOptions = useMemo(
    () => (Array.isArray(selectedCountryObj?.states) ? selectedCountryObj.states : []),
    [selectedCountryObj],
  );

  const selectedStateObj = useMemo(
    () => stateOptions.find(
      (state) => state?.name === form.state
        || state?.code === form.state
        || state?.name?.toLowerCase() === String(form.state || '').toLowerCase(),
    ) || null,
    [stateOptions, form.state],
  );

  const cityOptions = useMemo(
    () => (Array.isArray(selectedStateObj?.cities) ? selectedStateObj.cities : []),
    [selectedStateObj],
  );

  const handleFieldChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const serviceChipOptions = useMemo(() => {
    const catalog = getServiceOptionsForVendorType(form.vendor_type);
    const customSelected = parseServiceTags(form.serviceTags).filter(
      (tag) => !HALL_VENDOR_SERVICE_OPTIONS.some(
        (option) => option.toLowerCase() === tag.toLowerCase(),
      ),
    );
    return [...catalog, ...customSelected];
  }, [form.vendor_type, form.serviceTags]);

  const toggleServiceTag = useCallback((service) => {
    const label = String(service || '').trim();
    if (!label) return;
    setForm((prev) => {
      const selected = isServiceSelected(prev.serviceTags, label);
      return {
        ...prev,
        serviceTags: selected
          ? prev.serviceTags.filter((tag) => tag.toLowerCase() !== label.toLowerCase())
          : [...prev.serviceTags, label],
      };
    });
  }, []);

  const addCustomServiceTag = useCallback(() => {
    const label = customServiceInput.trim();
    if (!label) return;
    setForm((prev) => (
      isServiceSelected(prev.serviceTags, label)
        ? prev
        : { ...prev, serviceTags: [...prev.serviceTags, label] }
    ));
    setCustomServiceInput('');
  }, [customServiceInput]);

  const filtered = useMemo(() => {
    let list = [...rows];
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter((row) => (
        String(row.vendor_code || '').toLowerCase().includes(term)
        || String(row.name || '').toLowerCase().includes(term)
        || String(row.email || '').toLowerCase().includes(term)
        || String(row.phone || '').toLowerCase().includes(term)
        || String(row.services || '').toLowerCase().includes(term)
      ));
    }
    if (emailFilter.trim()) {
      const term = emailFilter.toLowerCase();
      list = list.filter((row) => String(row.email || '').toLowerCase().includes(term));
    }
    if (phoneFilter.trim()) {
      list = list.filter((row) => String(row.phone || '').includes(phoneFilter.trim()));
    }
    if (statusFilter) list = list.filter((row) => row.status === statusFilter);
    if (typeFilter) list = list.filter((row) => row.vendor_type === typeFilter);
    return list;
  }, [rows, search, emailFilter, phoneFilter, statusFilter, typeFilter]);

  const pagedRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const hasActiveFilters = Boolean(search || emailFilter || phoneFilter || statusFilter || typeFilter);

  const clearFilters = useCallback(() => {
    setSearch('');
    setEmailFilter('');
    setPhoneFilter('');
    setStatusFilter('');
    setTypeFilter('');
    setPage(0);
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setViewMode(false);
    setCustomServiceInput('');
    setForm(getFreshFormData());
    setDrawerOpen(true);
  };

  const openVendorDetail = useCallback((row) => {
    if (!row?.id) return;
    navigate(`/hall/vendor/${row.id}/`);
  }, [navigate]);

  const openView = useCallback((row) => {
    setEditingId(row.id);
    setViewMode(true);
    setCustomServiceInput('');
    setForm(buildFormFromRow(row));
    setDrawerOpen(true);
  }, [buildFormFromRow]);

  const openEdit = useCallback((row) => {
    setEditingId(row.id);
    setViewMode(false);
    setCustomServiceInput('');
    setForm(buildFormFromRow(row));
    setDrawerOpen(true);
  }, [buildFormFromRow]);

  const saveVendor = async () => {
    if (!isVendorFormValid(form)) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        vendor_type: form.vendor_type || 'other',
        phone: form.phone.trim(),
        phone_country_code: form.phone_country_code || '+91',
        email: form.email.trim(),
        gstin: form.gstin.trim(),
        country: form.country.trim(),
        state: form.state.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        services: serializeServiceTags(form.serviceTags),
        notes: form.notes.trim(),
        is_active: form.is_active,
      };
      if (editingId) {
        await updateHallVendor(editingId, payload);
        showMessage('success', 'Vendor updated successfully');
      } else {
        await createHallVendor(payload);
        showMessage('success', 'Vendor added successfully');
      }
      setDrawerOpen(false);
      await loadRows();
    } catch (err) {
      showMessage('error', getHallApiErrorMessage(err, 'Unable to save vendor.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteVendor = useCallback(async (row) => {
    if (!row?.id) return;
    try {
      await deleteHallVendor(row.id);
      showMessage('success', 'Vendor deleted successfully');
      await loadRows();
    } catch (err) {
      showMessage('error', getHallApiErrorMessage(err, 'Unable to delete vendor.'));
    }
  }, [loadRows, showMessage]);

  const columns = useMemo(() => [
    {
      field: 'vendor_code',
      label: 'Vendor ID',
      sortable: false,
      render: (_value, row) => (
        <Typography
          component="span"
          onClick={(event) => {
            event.stopPropagation();
            openVendorDetail(row);
          }}
          sx={{
            color: HALL_ACCENT,
            fontWeight: 800,
            fontSize: '0.82rem',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {row.vendor_code || `VND-${row.id}`}
        </Typography>
      ),
    },
    {
      field: 'name',
      label: 'Name',
      sortable: false,
      render: (_value, row) => (
        <Typography
          component="span"
          onClick={(event) => {
            event.stopPropagation();
            openVendorDetail(row);
          }}
          sx={{
            fontWeight: 700,
            fontSize: '0.88rem',
            color: 'text.primary',
            cursor: 'pointer',
            '&:hover': { color: HALL_ACCENT, textDecoration: 'underline' },
          }}
        >
          {row.name || '—'}
        </Typography>
      ),
    },
    {
      field: 'vendor_type',
      label: 'Type',
      sortable: false,
      render: (_value, row) => row.vendor_type_label || row.vendor_type || '—',
    },
    {
      field: 'email',
      label: 'Email',
      sortable: false,
      render: (_value, row) => row.email || '—',
    },
    {
      field: 'phone',
      label: 'Phone',
      sortable: false,
      render: (_value, row) => formatVendorPhoneDisplay(row),
    },
    {
      field: 'services',
      label: 'Services',
      sortable: false,
      render: (_value, row) => {
        const tags = parseServiceTags(row.services);
        if (!tags.length) return '—';
        return (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ maxWidth: 280 }}>
            {tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: alpha(HALL_ACCENT, isDark ? 0.16 : 0.1),
                  border: `1px solid ${alpha(HALL_ACCENT, 0.3)}`,
                }}
              />
            ))}
            {tags.length > 3 && (
              <Chip size="small" label={`+${tags.length - 3}`} sx={{ height: 22, fontSize: '0.7rem' }} />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'status',
      label: 'Status',
      sortable: false,
      render: (_value, row) => (
        <Chip size="small" label={row.status} sx={statusChipSx(row.status, isDark)} />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (_value, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" onClick={(event) => event.stopPropagation()}>
          <IconButton size="small" onClick={() => openVendorDetail(row)} sx={{ color: '#8ac9ff' }}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: '#4ce1b6' }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => deleteVendor(row)} sx={{ color: '#fb7185' }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ], [deleteVendor, isDark, openEdit, openVendorDetail]);

  const filterRow = useMemo(() => columns.map((column) => {
    switch (column.field) {
      case 'vendor_code':
        return (
          <TextField
            key={column.field}
            placeholder="VND-..."
            size="small"
            variant="standard"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%' }}
          />
        );
      case 'name':
        return <div key={column.field} />;
      case 'vendor_type':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={typeFilter}
              onChange={(event) => {
                setTypeFilter(event.target.value);
                setPage(0);
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              {VENDOR_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'email':
        return (
          <TextField
            key={column.field}
            placeholder="Filter email..."
            size="small"
            variant="standard"
            value={emailFilter}
            onChange={(event) => {
              setEmailFilter(event.target.value);
              setPage(0);
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ width: '100%' }}
          />
        );
      case 'phone':
        return (
          <TextField
            key={column.field}
            placeholder="Filter phone..."
            size="small"
            variant="standard"
            value={phoneFilter}
            onChange={(event) => {
              setPhoneFilter(event.target.value.replace(/[^\d+]/g, ''));
              setPage(0);
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ width: '100%' }}
          />
        );
      case 'status':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_VALUES.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'actions':
        return (
          <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {hasActiveFilters && (
              <Button variant="outlined" size="small" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </Box>
        );
      default:
        return <div key={column.field} />;
    }
  }), [
    columns,
    search,
    typeFilter,
    emailFilter,
    phoneFilter,
    statusFilter,
    hasActiveFilters,
    clearFilters,
  ]);

  const formInputSx = getHallFormInputSx(isDark);
  const drawerPaperSx = getHallDrawerPaperSx(isDark);

  const vendorTypeLabel = useMemo(
    () => VENDOR_TYPES.find((type) => type.value === form.vendor_type)?.label || form.vendor_type || '—',
    [form.vendor_type],
  );

  const phoneDisplay = useMemo(() => {
    const code = form.phone_country_code || selectedPhoneCountryCode?.dialCode || '';
    const number = form.phone || '';
    if (!number) return '—';
    return code ? `${code} ${number}` : number;
  }, [form.phone, form.phone_country_code, selectedPhoneCountryCode]);

  const locationDisplay = useMemo(() => {
    const parts = [form.city, form.state, form.country].map((part) => String(part || '').trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }, [form.city, form.country, form.state]);

  const switchToEditFromView = () => {
    setViewMode(false);
    setCustomServiceInput('');
  };

  return (
    <>
      <Paper sx={surfaceSx}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: tokens.text, fontWeight: 900, letterSpacing: '-0.01em' }}>
                Vendor Management
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.subtleText }}>
                Manage hall vendors with filters, actions, and pagination.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'center' },
                bgcolor: '#4ce1b6',
                color: '#031512',
                fontWeight: 900,
                '&:hover': { bgcolor: '#3bd1a7' },
              }}
            >
              Add Vendor
            </Button>
          </Stack>

          <DataTable
            columns={columns}
            data={pagedRows}
            loading={loading}
            pagination={{
              page,
              perPage: rowsPerPage,
              totalRecords: filtered.length,
            }}
            onPageChange={(nextPage) => setPage(nextPage)}
            onPerPageChange={(perPage) => {
              setRowsPerPage(Number(perPage || 10));
              setPage(0);
            }}
            emptyMessage={
              rows.length === 0
                ? 'No vendors found. Click "Add Vendor" to create one.'
                : 'No vendors match the selected filters.'
            }
            stickyHeader
            filterRow={filterRow}
            onRowClick={(row) => openVendorDetail(row)}
          />
        </Stack>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <StorefrontIcon sx={{ color: HALL_ACCENT }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
                {viewMode ? 'View Vendor' : editingId ? 'Edit Vendor' : 'Add Vendor'}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: isDark ? '#cbd5e1' : '#475569' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 1.5, boxSizing: 'border-box' }}>
            {viewMode ? (
              <Stack spacing={1.75}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
                    background: isDark
                      ? `linear-gradient(145deg, ${alpha('#0b1f1a', 0.95)} 0%, ${alpha('#0f172a', 0.98)} 55%, ${alpha('#082f2b', 0.9)} 100%)`
                      : `linear-gradient(145deg, #f0fdf8 0%, #ffffff 48%, #eefbf6 100%)`,
                  }}
                >
                  <Box
                    sx={{
                      height: 72,
                      background: `linear-gradient(120deg, ${alpha(HALL_ACCENT, isDark ? 0.45 : 0.7)} 0%, ${alpha('#0ea5a4', isDark ? 0.35 : 0.55)} 100%)`,
                    }}
                  />
                  <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ mt: -4 }}>
                      <Avatar
                        sx={{
                          width: 72,
                          height: 72,
                          fontWeight: 900,
                          fontSize: '1.35rem',
                          bgcolor: isDark ? '#0f766e' : HALL_ACCENT,
                          color: isDark ? '#ecfdf5' : '#031512',
                          border: `3px solid ${isDark ? '#0b1220' : '#fff'}`,
                          boxShadow: `0 10px 24px ${alpha('#0f172a', 0.18)}`,
                        }}
                      >
                        {vendorInitials(form.name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0, pb: 0.25 }}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: '1.25rem',
                            letterSpacing: '-0.02em',
                            color: isDark ? '#f8fafc' : '#0f172a',
                            lineHeight: 1.2,
                          }}
                        >
                          {form.name || 'Untitled vendor'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          {form.vendor_code ? (
                            <Chip
                              size="small"
                              icon={<BadgeOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
                              label={form.vendor_code}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(HALL_ACCENT, isDark ? 0.2 : 0.16),
                                border: `1px solid ${alpha(HALL_ACCENT, 0.4)}`,
                              }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            icon={<CategoryOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
                            label={vendorTypeLabel}
                            sx={{
                              fontWeight: 800,
                              bgcolor: alpha(HALL_ACCENT, isDark ? 0.2 : 0.16),
                              border: `1px solid ${alpha(HALL_ACCENT, 0.4)}`,
                              color: isDark ? '#ecfdf5' : '#065f46',
                            }}
                          />
                          <Chip
                            size="small"
                            label={form.is_active ? 'Active' : 'Inactive'}
                            sx={statusChipSx(form.is_active ? 'active' : 'inactive', isDark)}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                <VendorViewSection title="Contact" icon={<PhoneOutlinedIcon fontSize="small" />} isDark={isDark}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <VendorViewInfoTile isDark={isDark} icon={<PhoneOutlinedIcon fontSize="small" />} label="Phone" value={phoneDisplay} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <VendorViewInfoTile isDark={isDark} icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={form.email} />
                    </Grid>
                    <Grid item xs={12}>
                      <VendorViewInfoTile isDark={isDark} icon={<BadgeOutlinedIcon fontSize="small" />} label="GSTIN" value={form.gstin} />
                    </Grid>
                  </Grid>
                </VendorViewSection>

                <VendorViewSection title="Location" icon={<PlaceOutlinedIcon fontSize="small" />} isDark={isDark}>
                  <Stack spacing={1.25}>
                    <VendorViewInfoTile isDark={isDark} icon={<PlaceOutlinedIcon fontSize="small" />} label="City / State / Country" value={locationDisplay} />
                    <VendorViewInfoTile isDark={isDark} icon={<StorefrontIcon fontSize="small" />} label="Address" value={form.address} />
                  </Stack>
                </VendorViewSection>

                <VendorViewSection
                  title="Services"
                  icon={<WorkOutlineIcon fontSize="small" />}
                  isDark={isDark}
                  action={(
                    <Chip
                      size="small"
                      label={`${form.serviceTags.length} selected`}
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        bgcolor: alpha(HALL_ACCENT, isDark ? 0.22 : 0.16),
                        color: isDark ? '#ecfdf5' : '#065f46',
                      }}
                    />
                  )}
                >
                  {form.serviceTags.length ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {form.serviceTags.map((service) => (
                        <Chip
                          key={service}
                          label={service}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: alpha(HALL_ACCENT, isDark ? 0.24 : 0.16),
                            color: isDark ? '#ecfdf5' : '#064e3b',
                            border: `1px solid ${alpha(HALL_ACCENT, 0.45)}`,
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      No services selected for this vendor.
                    </Typography>
                  )}
                </VendorViewSection>

                {Boolean(String(form.notes || '').trim()) && (
                  <VendorViewSection title="Notes" icon={<NotesOutlinedIcon fontSize="small" />} isDark={isDark}>
                    <Typography
                      sx={{
                        color: isDark ? '#e2e8f0' : '#334155',
                        fontSize: '0.92rem',
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {form.notes}
                    </Typography>
                  </VendorViewSection>
                )}
              </Stack>
            ) : (
              <Grid container columnSpacing={HALL_DRAWER_FIELD_GAP} rowSpacing={HALL_DRAWER_FIELD_GAP} sx={{ width: '100%', maxWidth: '100%', m: 0 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Vendor Name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    fullWidth
                    sx={formInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Vendor Type"
                    value={form.vendor_type}
                    onChange={(e) => handleFieldChange('vendor_type', e.target.value)}
                    required
                    fullWidth
                    sx={formInputSx}
                  >
                    {VENDOR_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    required
                    value={form.phone}
                    onChange={(e) => handleFieldChange(
                      'phone',
                      normalizePhoneInputForCountry(e.target.value, form.phone_country_code),
                    )}
                    fullWidth
                    helperText={phoneHelperText}
                    error={Boolean(phoneHelperText)}
                    inputProps={{
                      maxLength: nationalPhoneMax,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    sx={formInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            ref={phoneCountryCodeAnchorRef}
                            onClick={() => setPhoneCountryCodeMenuOpen(true)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: isDark
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                              {selectedPhoneCountryCode?.flag || '🇮🇳'}
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                              {selectedPhoneCountryCode?.dialCode || form.phone_country_code || '+91'}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              ▼
                            </Typography>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Menu
                  anchorEl={phoneCountryCodeAnchorRef.current}
                  open={phoneCountryCodeMenuOpen}
                  onClose={() => {
                    setPhoneCountryCodeMenuOpen(false);
                    setPhoneCountryCodeSearch('');
                  }}
                  PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search country..."
                      value={phoneCountryCodeSearch}
                      onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  {COUNTRY_CODE_OPTIONS
                    .filter((country) => {
                      if (!phoneCountryCodeSearch) return true;
                      const searchTerm = phoneCountryCodeSearch.toLowerCase();
                      return (
                        country.name.toLowerCase().includes(searchTerm)
                        || country.dialCode.includes(searchTerm)
                        || country.code.toLowerCase().includes(searchTerm)
                      );
                    })
                    .map((country) => (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          handleFieldChange('phone_country_code', country.dialCode);
                          setPhoneCountryCodeMenuOpen(false);
                          setPhoneCountryCodeSearch('');
                        }}
                        selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                      >
                        <ListItemIcon>
                          <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={country.dialCode}
                          secondary={country.name}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </MenuItem>
                    ))}
                </Menu>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    value={form.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    type="email"
                    fullWidth
                    sx={formInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="GSTIN"
                    value={form.gstin}
                    onChange={(e) => handleFieldChange('gstin', e.target.value.toUpperCase())}
                    fullWidth
                    placeholder="Optional"
                    sx={formInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={COUNTRY_OPTIONS}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={countryFilterOptions}
                    value={selectedCountryObj}
                    onChange={(event, newValue) => {
                      handleFieldChange('country', newValue?.name || '');
                      handleFieldChange('state', '');
                      handleFieldChange('city', '');
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="Country" sx={formInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={stateOptions}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={stateFilterOptions}
                    value={selectedStateObj}
                    onChange={(event, newValue) => {
                      handleFieldChange('state', newValue?.name || '');
                      handleFieldChange('city', '');
                    }}
                    disabled={!selectedCountryObj}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="State"
                        helperText={form.state || selectedCountryObj ? '' : 'Select country first'}
                        sx={formInputSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={cityOptions}
                    getOptionLabel={(option) => (option != null ? String(option) : '')}
                    filterOptions={cityFilterOptions}
                    value={
                      cityOptions.find(
                        (city) => String(city || '').trim().toLowerCase() === String(form.city || '').trim().toLowerCase(),
                      ) || (form.city ? form.city : null)
                    }
                    onChange={(event, newValue) => {
                      handleFieldChange('city', newValue != null ? String(newValue) : '');
                    }}
                    disabled={!selectedStateObj}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="City" sx={formInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address"
                    value={form.address}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Optional"
                    sx={formInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.1)}`,
                      bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0f172a', 0.02),
                      p: 1.25,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                        Services
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        {form.serviceTags.length
                          ? `${form.serviceTags.length} selected`
                          : 'Tap chips to select'}
                      </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                      {serviceChipOptions.map((service) => {
                        const selected = isServiceSelected(form.serviceTags, service);
                        return (
                          <Chip
                            key={service}
                            label={service}
                            size="small"
                            clickable
                            onClick={() => toggleServiceTag(service)}
                            onDelete={selected ? () => toggleServiceTag(service) : undefined}
                            sx={{
                              fontWeight: selected ? 700 : 500,
                              bgcolor: selected
                                ? alpha(HALL_ACCENT, isDark ? 0.28 : 0.2)
                                : isDark ? alpha('#fff', 0.04) : '#fff',
                              color: selected
                                ? (isDark ? '#ecfdf5' : '#064e3b')
                                : (isDark ? '#cbd5e1' : '#334155'),
                              border: `1px solid ${
                                selected
                                  ? alpha(HALL_ACCENT, 0.55)
                                  : (isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.12))
                              }`,
                              '& .MuiChip-deleteIcon': {
                                color: selected ? HALL_ACCENT : 'inherit',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Custom service"
                        placeholder="Add another service"
                        value={customServiceInput}
                        onChange={(event) => setCustomServiceInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addCustomServiceTag();
                          }
                        }}
                        sx={formInputSx}
                      />
                      <Button
                        variant="outlined"
                        onClick={addCustomServiceTag}
                        disabled={!customServiceInput.trim()}
                        sx={{
                          minWidth: { sm: 110 },
                          borderColor: alpha(HALL_ACCENT, 0.5),
                          color: isDark ? '#e2e8f0' : '#0f172a',
                          fontWeight: 800,
                        }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    fullWidth
                    multiline
                    minRows={3}
                    sx={formInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#e5e7eb' : '#111827' }}>
                      Status
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      Inactive
                    </Typography>
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                      color="primary"
                      inputProps={{ 'aria-label': 'Vendor active status' }}
                    />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      Active
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              p: 1.5,
              borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
              boxSizing: 'border-box',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDrawerOpen(false)}
              disabled={saving}
              sx={{ borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#0f172a', 0.16), color: isDark ? '#e2e8f0' : '#334155', fontWeight: 900 }}
            >
              {viewMode ? 'Close' : 'Cancel'}
            </Button>
            {viewMode ? (
              <Button
                variant="contained"
                onClick={switchToEditFromView}
                startIcon={<EditIcon />}
                sx={{ bgcolor: HALL_ACCENT, color: '#031512', fontWeight: 900, '&:hover': { bgcolor: '#3bd1a7' } }}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={saveVendor}
                disabled={saving || !isVendorFormValid(form)}
                startIcon={<AddIcon />}
                sx={{ bgcolor: HALL_ACCENT, color: '#031512', fontWeight: 900, '&:hover': { bgcolor: '#3bd1a7' } }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </Box>

      </Drawer>
    </>
  );
}
